import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { convertToHectares } from 'utils/unitConversion';
import { STEP_TYPE_TO_COST_CATEGORY } from 'pages/FarmsPage/Workspace/Insights/costCategories';

/**
 * Summary 2 (report-style Insights lens, in UAT alongside the original
 * Summary lens) — small shared helpers used only by this lens's own compute
 * files. The originals these mirror (`computeAnalysis.ts`'s
 * stepType→category map) are private to their files, so rather than
 * extracting them out — which would touch code backing the still-live
 * original Summary tab — the same logic is kept here, independently.
 */

export interface ResolvedCrop {
  crop: string;
  variety: string;
}

/**
 * Crop/variety for an input — always the document-level `input.crop`
 * attribute, never inferred from a Seed step's own item/item2 fields (those
 * describe the specific seed product applied, which is a different concept
 * from "what crop is this plan for"). Every crop-grouping/selecting feature
 * in this lens (Area Summary, Cost by Crop, Plan vs Actual, the spend
 * timeline's crop selector, and Seed Summary's own Crop/Variety grouping)
 * goes through this one resolver so they all agree.
 */
export const resolveInputCrop = (input: IInput): ResolvedCrop => ({
  crop: input.crop.cropName,
  variety: input.crop.variety?.varietyName ?? '',
});

/** Step type → cost category — the shared mapping from costCategories.ts (Seed dress counts as Seed). */
export const STEP_TYPE_TO_SUMMARY_ENTITY = STEP_TYPE_TO_COST_CATEGORY;

/** Crop/variety row label — e.g. "Wheat, APH", or just "Wheat" with no variety. */
export const resolveCropGroupLabel = (crop: string, variety: string): string =>
  variety ? `${crop}, ${variety}` : crop || 'Unspecified Crop';

export interface CropGroup {
  key: string;
  label: string;
  inputs: IInput[];
  /** Earliest startDate among the group's constituent inputs — null when none has one. Used to default a crop selector to the oldest season. */
  earliestStartDate: string | null;
}

/**
 * Group a scope's inputs by resolved crop/variety — shared by any section
 * that needs to isolate one crop's plans out of a scope that can span
 * several (e.g. a paddock's multi-season plan history).
 */
export const groupInputsByCrop = (inputs: IInput[]): CropGroup[] => {
  const groups = new Map<string, CropGroup>();

  for (const input of inputs) {
    const resolved = resolveInputCrop(input);
    const label = resolveCropGroupLabel(resolved.crop, resolved.variety);
    const existing = groups.get(label) ?? {
      key: label,
      label,
      inputs: [],
      earliestStartDate: null,
    };
    existing.inputs.push(input);
    if (
      input.startDate &&
      (!existing.earliestStartDate ||
        input.startDate < existing.earliestStartDate)
    ) {
      existing.earliestStartDate = input.startDate;
    }
    groups.set(label, existing);
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
};

/** The group with the earliest startDate among its inputs — groups with no dated input sort last. */
export const resolveOldestCropGroup = (
  groups: CropGroup[]
): CropGroup | undefined =>
  groups.reduce<CropGroup | undefined>((best, group) => {
    if (!best) return group;
    if (!group.earliestStartDate) return best;
    if (!best.earliestStartDate) return group;
    return group.earliestStartDate < best.earliestStartDate ? group : best;
  }, undefined);

/**
 * A single input's area, sourced directly from its zone (unaffected by
 * which task category — Plan/Actual — is in view). Mirrors
 * computeAnalysis.ts's private `resolveInputAreaHa`.
 */
export const resolveInputAreaHa = (input: IInput, gUnits: GUnit[]): number => {
  const zoneArea = input.zone?.zoneArea ?? 0;
  if (zoneArea <= 0) return 0;
  return (
    convertToHectares(zoneArea, input.zone?.areaUnit ?? null, gUnits) ??
    zoneArea
  );
};

/** Summed area (ha) across a set of inputs — e.g. every input behind one crop group. */
export const resolveInputsAreaHa = (
  inputs: IInput[],
  gUnits: GUnit[]
): number =>
  inputs.reduce((sum, input) => sum + resolveInputAreaHa(input, gUnits), 0);

/** TSV-join headers + rows into clipboard-ready text — paired with `useCopyToClipboard` in every Summary 2 table's copy button. */
export const buildTsv = (
  headers: string[],
  rows: (string | number)[][]
): string => [headers, ...rows].map((cols) => cols.join('\t')).join('\n');
